import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate("/courses", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/courses";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="relative flex items-center gap-2">
          <img src="/logo.png" alt="Classroom to Corporate" className="h-10 w-10 object-contain" />
          <span className="font-heading text-xl font-bold">Classroom to <span className="text-amber-600">Corporate</span></span>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl font-extrabold leading-tight">
            {t("login.heroTitle")}
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed">
            {t("login.heroSubtitle")}
          </p>
        </div>
      </div>

      <div className="flex flex-col p-8">
        <div className="flex justify-end">
          <LanguageSwitcher />
        </div>
        <div className="flex flex-1 items-center justify-center">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-10 w-10 object-contain" />
            <span className="font-heading text-xl font-bold text-slate-900">Classroom to <span className="text-amber-600">Corporate</span></span>
          </div>
          <h2 className="font-heading text-3xl font-bold text-slate-900">{t("login.welcome")}</h2>
          <p className="mt-2 text-slate-500">{t("login.subtitle")}</p>

          <button
            data-testid="google-login-button"
            onClick={handleGoogleLogin}
            className="group mt-8 w-full flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 font-semibold text-slate-800 shadow-sm transition-all hover:border-slate-300 hover:shadow-md"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt=""
              width={20}
              height={20}
            />
            {t("login.continueWithGoogle")}
            <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
          </button>

          <p className="mt-6 text-xs text-slate-400 leading-relaxed">
            {t("login.terms")}
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
